import { useMemo } from "@wordpress/element";

/**
 * Convert spacing object or JSON string to CSS shorthand string
 * @param {Object|string} spacing - Object with top, right, bottom, left values OR JSON string
 * @returns {string|undefined} CSS shorthand string or undefined if all empty
 */
export const useSpacingValue = (spacing) => {
    return useMemo(() => {
        if (!spacing) return undefined;

        let spacingObj;

        // If string, parse it first
        if (typeof spacing === "string") {
            try {
                spacingObj = JSON.parse(spacing);

                // Ensure it's an object after parsing
                if (typeof spacingObj !== "object" || spacingObj === null) {
                    return undefined;
                }
            } catch (error) {
                console.error("Failed to parse spacing string:", error);
                return undefined;
            }
        } else if (typeof spacing === "object" && spacing !== null) {
            spacingObj = spacing;
        } else {
            return undefined;
        }

        const { top = "", right = "", bottom = "", left = "" } = spacingObj;

        // Normalize empty values to "0"
        const normalizedTop = top || "0";
        const normalizedRight = right || "0";
        const normalizedBottom = bottom || "0";
        const normalizedLeft = left || "0";

        // If all values are "0", return undefined
        if (
            normalizedTop === "0" &&
            normalizedRight === "0" &&
            normalizedBottom === "0" &&
            normalizedLeft === "0"
        ) {
            return undefined;
        }

        // Optimize shorthand notation
        // If all sides are equal: "5px"
        if (
            normalizedTop === normalizedRight &&
            normalizedRight === normalizedBottom &&
            normalizedBottom === normalizedLeft
        ) {
            return normalizedTop;
        }

        // If top/bottom equal and left/right equal: "5px 10px"
        if (
            normalizedTop === normalizedBottom &&
            normalizedRight === normalizedLeft
        ) {
            return `${normalizedTop} ${normalizedRight}`;
        }

        // If left/right equal: "5px 10px 15px"
        if (normalizedRight === normalizedLeft) {
            return `${normalizedTop} ${normalizedRight} ${normalizedBottom}`;
        }

        // Full notation: "5px 10px 15px 20px"
        return `${normalizedTop} ${normalizedRight} ${normalizedBottom} ${normalizedLeft}`;
    }, [spacing]);
};
